import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { apiSlice } from '../store/api/apiSlice';
import { workspaceApi } from '../store/api/workspaceApi';
import { financeApi } from '../store/api/financeApi';
import { notificationListTag } from '../store/api/notificationsApi';
import { operationalRealtimeClient, RealtimeProjectionMessage } from './operationalRealtimeClient';
import { isRealtimeClientEnabled } from './realtimeConfig';

const CONFLICT_EVENT_TYPES = new Set([
  'ROUTE_VERSION_CONFLICT',
  'STOP_PATCH_GRAPH_STALE',
  'STOP_PATCH_EXECUTED_REORDER_FORBIDDEN'
]);

function applyProjection(dispatch: ReturnType<typeof useDispatch>, msg: RealtimeProjectionMessage): void {
  const eventType = msg.event?.eventType || msg.projection?.payload?.eventType;
  if (eventType && CONFLICT_EVENT_TYPES.has(eventType)) {
    const routeId = msg.projection.payload.routeId;
    dispatch(
      apiSlice.util.invalidateTags(
        routeId ? [{ type: 'Route', id: routeId }, 'Workspace'] : ['Route', 'Workspace']
      )
    );
    return;
  }

  const tags = msg.projection?.payload?.invalidateTags ?? [];
  if (tags.includes('Workspace')) {
    dispatch(workspaceApi.util.invalidateTags(['Workspace']));
  }
  if (tags.includes('Finance')) {
    dispatch(financeApi.util.invalidateTags(['Finance']));
  }
  if (tags.includes('Notification')) {
    dispatch(apiSlice.util.invalidateTags([notificationListTag]));
  }
  if (tags.includes('Route')) {
    const routeId = msg.projection.payload.routeId;
    dispatch(
      apiSlice.util.invalidateTags(
        routeId ? [{ type: 'Route', id: routeId }] : ['Route']
      )
    );
  }
}

export function useOperationalRealtime(userId?: number): { connected: boolean } {
  const dispatch = useDispatch();
  const [connected, setConnected] = useState(false);
  const channelsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!isRealtimeClientEnabled() || !userId) return;

    const channels = [
      `workspace:${userId}`,
      `dashboard:${userId}`,
      `notifications:${userId}`,
      `finance:${userId}`
    ];
    channelsRef.current = channels;
    operationalRealtimeClient.connect(userId, channels);

    const unsub = operationalRealtimeClient.onProjection((msg) => {
      applyProjection(dispatch, msg);
    });

    const tick = setInterval(() => {
      setConnected(operationalRealtimeClient.isConnected());
    }, 1000);

    return () => {
      clearInterval(tick);
      unsub();
      operationalRealtimeClient.disconnect();
    };
  }, [dispatch, userId]);

  return { connected };
}
